import { useQuery } from "@tanstack/react-query";
import { v2SearchClient } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";

/**
 * One clip of an assembled reel. Field names match /v2/search wherever the two
 * overlap; index, offset, duration, start and the scores are the additions.
 *
 * TWO TIMELINES LIVE ON THIS ROW AND THEY ARE NOT INTERCHANGEABLE. `start` is
 * the moment's position in the source game asset, which is what a player
 * showing the whole game seeks to. `offset` is its position in the assembled
 * reel, which is what a player showing the reel seeks to. MomentsStrip's
 * `timeline` prop picks between them; picking wrong marks the wrong card and
 * seeks minutes away from the moment.
 */
export type MomentClip = {
  index: number;
  game_event_id: number;
  event_type: string;
  period: number | null;
  offset: number;
  duration: number;
  start: number | null;
  /**
   * The analytics side's team ids, which are TPE ids. The watch page holds the
   * tenant's own home_team_id_tpe and away_team_id_tpe, so these are what say
   * which crest belongs on which side rather than assuming the two agree.
   */
  home_team_id: number | null;
  away_team_id: number | null;
  home_score: number | null;
  away_score: number | null;
  game_date: string | null;
  home_team_name: string | null;
  away_team_name: string | null;
  player_first_name: string | null;
  player_last_name: string | null;
  player_position: string | null;
  player_jersey: number | null;
  team_name: string | null;
  thumbnail_url: string | null;
};

/**
 * Which reel to list the moments of.
 *
 * `date` is the moment the reel was built and is part of the server's cache key
 * (`${TENANT}:CONDENSED_GAME:${gameIds}:${date}`). Send it when the player is
 * playing a url that carries one, and leave it off when the player is not
 * playing a reel at all. See the `date` note on useMomentClips.
 */
export type ReelSource =
  | { kind: "condensed"; gameIds: string; date?: string | null }
  /**
   * A goals reel, carrying the reel url's ENTIRE query rather than picked
   * fields.
   *
   * Every parameter that shapes the row set has to travel, and picking them by
   * hand already got this wrong once: the url says `page=1&limit=800` and the
   * service defaults to `limit=10`, so leaving them off asked about 10 of the
   * 15 goals, the offsets no longer described the reel, and the endpoint
   * correctly answered with nothing. Forwarding the query verbatim is what
   * makes "the clips of the reel that is playing" true by construction.
   */
  | { kind: "assembly"; gameIds: string; search: string };

type ClipsResponse = { data?: { list?: MomentClip[] } };

/**
 * The clips in a reel, in reel order, with each one's position in it.
 *
 * ON `date`: it decides which build of the reel the answer describes. The watch
 * page sends none, because its player plays the raw game asset rather than a
 * reel, and inventing one there could resolve to a playlist built at a
 * different moment than the offsets it went on to use. The short-view page
 * sends the one out of the url its player is actually loading, because there
 * the offsets have to describe that exact reel. Both are the same rule: send
 * what the player is playing.
 */
export function useMomentClips(source: ReelSource | null) {
  const gameIds = source?.gameIds ? String(source.gameIds) : null;
  const date = source?.kind === "condensed" ? source.date || null : null;
  const search = source?.kind === "assembly" ? source.search : null;

  return useQuery({
    queryKey: [
      "v2",
      "moments",
      "clips",
      source?.kind ?? null,
      gameIds,
      date,
      search,
    ],
    enabled: Boolean(gameIds),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const token = getCurrentAuth()?.AccessToken;
      let url: string;
      if (search != null) {
        url = `${APIRoutes.AssemblyClips}?${search}`;
      } else {
        const params = new URLSearchParams({ game_ids: String(gameIds) });
        if (date) params.set("date", date);
        url = `${APIRoutes.CondensedGameClips}?${params}`;
      }
      const res = await v2SearchClient.get<ClipsResponse>(url, {
        headers: token ? { Authorization: `bearer ${token}` } : {},
        signal,
      });
      return res.data?.data?.list ?? [];
    },
  });
}
